import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Chip,
  Paper,
  Button,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";

/* ================= SERVICE DATA ================= */

const SERVICES = {
  "ZNX-COM-11": {
    title: "Common Booking",
    en: "Reserve a service slot. Scope, timeline, and pricing are finalized after consultation.",
    ta: "Service slot reserve செய்யலாம். Consultation பிறகு scope மற்றும் pricing உறுதி செய்யப்படும்.",
  },
  "ZNX-WEB-01": {
    title: "Website Development",
    en: "Business websites, portfolio sites, landing pages, and custom web solutions.",
    ta: "வணிக இணையதளங்கள், portfolio, landing page மற்றும் custom web தீர்வுகள்.",
  },
  "ZNX-APP-02": {
    title: "Mobile App Development",
    en: "Android and iOS mobile applications with modern UI and performance.",
    ta: "Android மற்றும் iOS mobile applications – modern UI மற்றும் performance.",
  },
  "ZNX-SW-03": {
    title: "Custom Software Development",
    en: "Tailored software solutions built to match your business workflow.",
    ta: "உங்கள் வணிக தேவைக்கு ஏற்ப custom software தீர்வுகள்.",
  },
  "ZNX-MNT-04": {
    title: "Maintenance & Support",
    en: "Bug fixes, upgrades, monitoring, and long-term technical support.",
    ta: "Bug fix, update, monitoring மற்றும் தொடர்ந்த technical support.",
  },
  "ZNX-SUITE-05": {
    title: "Complete Digital Suite",
    en: "Website, mobile app, and software combined into one integrated solution.",
    ta: "Website, mobile app மற்றும் software இணைந்த முழுமையான digital solution.",
  },
};

/* ================= COMPONENT ================= */

const SupportChatBot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: "bot",
      text: "Welcome to Zolang Nexora Support.\nSelect a service category to view details.",
    },
  ]);

  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const showCategories = () => {
    setMessages((prev) => [
      ...prev,
      { from: "bot", type: "categories" },
    ]);
  };

  const showService = (code) => {
    const s = SERVICES[code];
    setMessages((prev) => [
      ...prev,
      {
        from: "bot",
        text: `${code} – ${s.title}\n\n${s.en}\n\n${s.ta}`,
      },
    ]);
  };

  return (
    <>
      {/* ================= FLOAT BUTTON ================= */}
      {!open && (
        <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              width: 60,
              height: 60,
              background: "#ff4d4d",
              color: "#fff",
              zIndex: 13000,
              boxShadow: "0 12px 30px rgba(0,0,0,0.4)",
              "&:hover": { background: "#e64545" },
            }}
          >
            <ChatIcon />
          </IconButton>
        </motion.div>
      )}

      {/* ================= CHAT PANEL ================= */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999 }}
          >
            <Paper
              sx={{
                width: { xs: "92vw", sm: 420 },
                height: 560,
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 2,
                  background: "#ff4d4d",
                  color: "#fff",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography fontWeight={800}>Zolang Nexora Support</Typography>
                <IconButton onClick={() => setOpen(false)} sx={{ color: "#fff" }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, p: 2, overflowY: "auto", background: "#0b1020" }}>
                <Stack spacing={2}>
                  {messages.map((m, i) => (
                    <Box key={i}>
                      {m.type === "categories" ? (
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          justifyContent="center"
                          gap={1.5}
                        >
                          {Object.keys(SERVICES).map((code) => (
                            <Chip
                              key={code}
                              label={code}
                              onClick={() => showService(code)}
                              sx={{ fontWeight: 700, minWidth: 150 }}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            background: "rgba(255,255,255,0.12)",
                            color: "#fff",
                            px: 2,
                            py: 1.4,
                            borderRadius: 2.5,
                          }}
                        >
                          <Typography fontSize="0.9rem" whiteSpace="pre-line">
                            {m.text}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                  <div ref={endRef} />
                </Stack>
              </Box>

              {/* Actions */}
              <Box sx={{ p: 2 }}>
                <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1.5}>
                  <Chip label="Service Categories" onClick={showCategories} />
                  <Chip label="Advance Payment" onClick={() =>
                    setMessages((p) => [...p, {
                      from: "bot",
                      text: "A 30% advance confirms booking and resource allocation.",
                    }])
                  } />
                  <Chip label="Pricing Info" onClick={() =>
                    setMessages((p) => [...p, {
                      from: "bot",
                      text: "Final pricing is confirmed after consultation based on scope.",
                    }])
                  } />
                </Stack>
              </Box>

              {/* Footer */}
              <Box sx={{ textAlign: "center", pb: 1 }}>
                <Button startIcon={<PhoneIcon />} href="tel:+916382585971">
                  Call +91 63825 85971
                </Button>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Powered by Zolang Nexora Softwares
                </Typography>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SupportChatBot;
